import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArchiveBoxIcon, MagnifyingGlassIcon, ExclamationTriangleIcon,
  CheckCircleIcon, ArrowPathIcon, ArchiveBoxArrowDownIcon,
} from '@heroicons/react/24/outline';

const curYear = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .yc * { box-sizing:border-box; }
  .yc { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ycFade .4s ease both; }
  @keyframes ycFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .yc-hdr { margin-bottom:1.5rem; }
  .yc-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .yc-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover  { background:#2d5ef0; }
  .btn-red   { background:#ef4444; color:white; } .btn-red:hover   { background:#dc2626; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1.2rem; }
  .panel-title { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:1rem; }
  .setup-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(170px,1fr)); gap:12px; margin-bottom:1.2rem; }
  .fl { display:flex; flex-direction:column; gap:4px; }
  .fl-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fl-select,.fl-input { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; }
  .fl-select:focus,.fl-input:focus { border-color:#3b6ef8; background:white; }
  /* Delete what toggles */
  .what-row { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .what-btn { display:flex; align-items:center; gap:7px; padding:8px 14px; border-radius:10px; border:1.5px solid #e0e4f0; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; background:white; color:#6b7194; transition:.15s; }
  .what-btn.on { border-color:#ef4444; background:#fef2f2; color:#ef4444; }
  /* Preview cards */
  .preview-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:10px; margin-bottom:1.2rem; }
  .prev-card { background:#f8f9fc; border-radius:12px; border:1px solid #e8ebf4; padding:14px; text-align:center; }
  .prev-val  { font-size:1.8rem; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1; }
  .prev-label{ font-size:.68rem; color:#9094a8; font-weight:600; margin-top:4px; }
  .warning-box { background:#fffbeb; border:1px solid #fde68a; border-radius:12px; padding:14px 16px; font-size:.8rem; color:#92400e; margin-bottom:1.2rem; display:flex; gap:10px; align-items:flex-start; }
  .warning-box svg { width:20px; height:20px; flex-shrink:0; margin-top:1px; }
  .success-box { background:#f0fdf4; border:1px solid #a7f3d0; border-radius:12px; padding:16px; text-align:center; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#ef4444; border-radius:50%; animation:spin .7s linear infinite; flex-shrink:0; }
  @keyframes spin { to{transform:rotate(360deg)} }
  .confirm-input { font-family:'JetBrains Mono',monospace; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; width:100%; margin-top:8px; }
  .confirm-input:focus { border-color:#ef4444; background:white; }
  .confirm-hint { font-size:.72rem; color:#9094a8; margin-top:5px; }
  .arch-list { display:flex; flex-direction:column; gap:6px; margin-bottom:12px; max-height:320px; overflow-y:auto; }
  .arch-row { display:flex; align-items:center; gap:10px; padding:9px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; }
  .arch-row input[type=checkbox] { width:16px; height:16px; flex-shrink:0; cursor:pointer; }
  .arch-title { font-size:.8rem; font-weight:600; color:#0f1123; }
  .arch-meta  { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .arch-empty { font-size:.8rem; color:#9094a8; text-align:center; padding:1.5rem; }
  .arch-section-title { font-size:.74rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.05em; margin:14px 0 8px; }
  @media (max-width:600px) { .yc-title{font-size:1.1rem;} .setup-grid{grid-template-columns:1fr 1fr;} }
`;

export default function YearEndCleanup({ readOnly=false }) {
  const apiPrefix = readOnly ? '/coordinator' : '/admin';

  const [classes,      setClasses]     = useState([]);
  const [classId,      setClassId]     = useState('');
  const [section,      setSection]     = useState('');
  const [academicYear, setAcademicYear]= useState(curYear());
  const [deleteWhat,   setDeleteWhat]  = useState('all');
  const [preview,      setPreview]     = useState(null);
  const [previewing,   setPreviewing]  = useState(false);
  const [confirm,      setConfirm]     = useState('');
  const [executing,    setExecuting]   = useState(false);
  const [result,       setResult]      = useState(null);

  const [showArchived, setShowArchived]= useState(false);
  const [archived,     setArchived]    = useState(null);
  const [loadingArch,  setLoadingArch] = useState(false);
  const [selMaterials, setSelMaterials]= useState([]);
  const [selQuizzes,   setSelQuizzes]  = useState([]);
  const [restoring,    setRestoring]   = useState(false);

  useEffect(() => {
    api.get(`${apiPrefix}/classes`).then(r => setClasses(r.data?.classes||r.data||[])).catch(()=>{});
  }, []);

  const sections = classes.find(c=>String(c._id)===String(classId))?.sections||[];
  const className = classes.find(c=>String(c._id)===String(classId))?.name||'';

  const CONFIRM_PHRASE = 'ARCHIVE';

  const handlePreview = async () => {
    if (!classId||!section||!academicYear) return toast.error('Select class, section and year');
    setPreviewing(true); setPreview(null); setResult(null); setConfirm('');
    setShowArchived(false); setArchived(null);
    try {
      const { data } = await api.get(`${apiPrefix}/cleanup/preview?classId=${classId}&section=${section}&academicYear=${academicYear}`);
      setPreview(data);
    } catch { toast.error('Failed to load preview'); }
    finally { setPreviewing(false); }
  };

  const handleExecute = async () => {
    if (confirm !== CONFIRM_PHRASE) return toast.error(`Type ${CONFIRM_PHRASE} to confirm`);
    setExecuting(true);
    try {
      const { data } = await api.post('/admin/cleanup/execute', {
        classId, section, academicYear, deleteWhat,
      });
      setResult(data);
      setPreview(null);
      setConfirm('');
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setExecuting(false); }
  };

  const handleRequestCleanup = async () => {
    if (!classId||!section||!academicYear) return toast.error('Select class, section and year');
    try {
      await api.post('/coordinator/request/cleanup', {
        classId, className, section, academicYear, deleteWhat,
      });
      toast.success('Archive request submitted — admin will review');
    } catch { toast.error('Failed to submit request'); }
  };

  const reset = () => {
    setPreview(null); setResult(null); setConfirm('');
    setClassId(''); setSection(''); setAcademicYear(curYear());
    setShowArchived(false); setArchived(null);
  };

  const whatLabel = deleteWhat==='materials' ? 'Materials only'
                  : deleteWhat==='quizzes'   ? 'AI Quizzes only'
                  : 'Materials + AI Quizzes';

  const loadArchived = async () => {
    if (!classId||!section) return toast.error('Select class and section');
    setShowArchived(true); setLoadingArch(true);
    setSelMaterials([]); setSelQuizzes([]);
    try {
      const { data } = await api.get(`${apiPrefix}/cleanup/archived?classId=${classId}&section=${section}`);
      setArchived(data);
    } catch { toast.error('Failed to load archived items'); }
    finally { setLoadingArch(false); }
  };

  const toggleSel = (list, setList, id) => {
    setList(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };

  const handleUnarchive = async () => {
    if (!selMaterials.length && !selQuizzes.length) return toast.error('Select at least one item to restore');
    setRestoring(true);
    try {
      const { data } = await api.put(`${apiPrefix}/cleanup/unarchive`, {
        materialIds: selMaterials, quizIds: selQuizzes,
      });
      toast.success(data.message);
      loadArchived();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to restore'); }
    finally { setRestoring(false); }
  };

  return (
    <div className="yc"><style>{css}</style>
      <div className="yc-hdr">
        <div className="yc-title">🧹 Year-End Cleanup</div>
        <div className="yc-sub">Archive materials and AI quizzes after class promotion — hidden from newly promoted students, restorable anytime</div>
      </div>

      <div className="panel">
        <div className="panel-title">Select Class to Clean Up</div>
        <div className="setup-grid">
          <div className="fl">
            <label className="fl-label">Class</label>
            <select className="fl-select" value={classId} onChange={e=>{setClassId(e.target.value);setSection('');setPreview(null);setResult(null);}}>
              <option value="">Select class…</option>
              {sortByName(classes).map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div className="fl">
            <label className="fl-label">Section</label>
            <select className="fl-select" value={section} onChange={e=>{setSection(e.target.value);setPreview(null);setResult(null);}} disabled={!classId}>
              <option value="">Select section…</option>
              {sortAsc(sections).map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="fl">
            <label className="fl-label">Academic Year</label>
            <input className="fl-input" value={academicYear} onChange={e=>{setAcademicYear(e.target.value);setPreview(null);setResult(null);}} placeholder="2024-2025"/>
          </div>
        </div>

        {/* What to archive */}
        <div style={{fontSize:'.74rem',fontWeight:700,color:'#6b7194',marginBottom:8}}>What to archive:</div>
        <div className="what-row">
          {[
            { key:'all',       label:'📚 Materials + 🧠 Quizzes' },
            { key:'materials', label:'📚 Materials only' },
            { key:'quizzes',   label:'🧠 Quizzes only' },
          ].map(w => (
            <button key={w.key} className={`what-btn${deleteWhat===w.key?' on':''}`} onClick={()=>setDeleteWhat(w.key)}>
              {w.label}
            </button>
          ))}
        </div>

        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-blue" onClick={handlePreview} disabled={previewing||!classId||!section||!academicYear}>
            {previewing ? <><div className="spinner" style={{borderTopColor:'white'}}/>Checking…</> : <><MagnifyingGlassIcon/>Preview</>}
          </button>
          <button className="btn btn-ghost" onClick={loadArchived} disabled={!classId||!section}>
            <ArchiveBoxArrowDownIcon/>View Archived
          </button>
          {result && <button className="btn btn-ghost btn-sm" onClick={reset}><ArrowPathIcon/>Clean Another</button>}
        </div>
      </div>

      {/* Archived items — view + unarchive */}
      {showArchived && (
        <div className="panel">
          <div className="panel-title">Archived Items — {className} · Section {section}</div>
          {loadingArch ? (
            <div className="arch-empty"><div className="spinner" style={{margin:'0 auto 8px'}}/>Loading…</div>
          ) : !archived || (!archived.materials?.length && !archived.quizzes?.length) ? (
            <div className="arch-empty">Nothing archived for this class/section yet.</div>
          ) : (
            <>
              {archived.materials?.length > 0 && (
                <>
                  <div className="arch-section-title">📚 Materials ({archived.materials.length})</div>
                  <div className="arch-list">
                    {archived.materials.map(m => (
                      <label key={m.id} className="arch-row">
                        <input type="checkbox" checked={selMaterials.includes(m.id)} onChange={()=>toggleSel(selMaterials,setSelMaterials,m.id)}/>
                        <div>
                          <div className="arch-title">{m.title}</div>
                          <div className="arch-meta">{m.subject||'—'} · Archived {m.archived_at ? new Date(m.archived_at).toLocaleDateString() : '—'}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
              {archived.quizzes?.length > 0 && (
                <>
                  <div className="arch-section-title">🧠 AI Quizzes ({archived.quizzes.length})</div>
                  <div className="arch-list">
                    {archived.quizzes.map(q => (
                      <label key={q.id} className="arch-row">
                        <input type="checkbox" checked={selQuizzes.includes(q.id)} onChange={()=>toggleSel(selQuizzes,setSelQuizzes,q.id)}/>
                        <div>
                          <div className="arch-title">{q.title}</div>
                          <div className="arch-meta">{q.subject||'—'} · Archived {q.archived_at ? new Date(q.archived_at).toLocaleDateString() : '—'}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
              <button className="btn btn-blue" onClick={handleUnarchive} disabled={restoring||(!selMaterials.length&&!selQuizzes.length)}>
                {restoring ? <><div className="spinner" style={{borderTopColor:'white'}}/>Restoring…</> : <><ArchiveBoxArrowDownIcon/>Unarchive Selected</>}
              </button>
            </>
          )}
        </div>
      )}

      {/* Preview */}
      {preview && !result && (
        <div className="panel">
          <div className="panel-title">Preview — {className} · Section {section} · {academicYear}</div>

          <div className="preview-grid">
            {(deleteWhat==='all'||deleteWhat==='materials') && (
              <div className="prev-card">
                <div className="prev-val" style={{color: preview.materialCount>0?'#ef4444':'#10c98e'}}>{preview.materialCount}</div>
                <div className="prev-label">📚 Materials</div>
              </div>
            )}
            {(deleteWhat==='all'||deleteWhat==='quizzes') && (
              <div className="prev-card">
                <div className="prev-val" style={{color: preview.quizCount>0?'#ef4444':'#10c98e'}}>{preview.quizCount}</div>
                <div className="prev-label">🧠 AI Quizzes</div>
              </div>
            )}
            <div className="prev-card">
              <div className="prev-val" style={{color: preview.total>0?'#ef4444':'#10c98e'}}>{preview.total}</div>
              <div className="prev-label">Total to Archive</div>
            </div>
          </div>

          {preview.total === 0 ? (
            <div style={{background:'#f0fdf4',border:'1px solid #a7f3d0',borderRadius:10,padding:'12px 14px',fontSize:'.8rem',color:'#065f46'}}>
              ✓ Nothing to archive — this class/section/year is already clean.
            </div>
          ) : readOnly ? (
            <>
              <div className="warning-box">
                <ExclamationTriangleIcon/>
                <div>This will archive <strong>{preview.total} item(s)</strong> ({whatLabel}) for <strong>{className} - {section}</strong> ({academicYear}) — hidden from students, restorable anytime. Admin must approve this request.</div>
              </div>
              <button className="btn btn-ghost" onClick={handleRequestCleanup}>
                📤 Request Archive
              </button>
            </>
          ) : (
            <>
              <div className="warning-box">
                <ExclamationTriangleIcon/>
                <div>
                  This will archive <strong>{preview.total} item(s)</strong> ({whatLabel}) for <strong>{className} - {section}</strong> ({academicYear}) — they'll be hidden from students in this class/section.
                  <br/>Nothing is deleted: you can restore any of it anytime from <strong>View Archived</strong>. Student results and attendance are NOT affected.
                </div>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:'.76rem',fontWeight:600,color:'#6b7194'}}>Type <strong style={{fontFamily:'JetBrains Mono',color:'#3b6ef8'}}>{CONFIRM_PHRASE}</strong> to confirm:</div>
                <input className="confirm-input" placeholder={CONFIRM_PHRASE} value={confirm} onChange={e=>setConfirm(e.target.value.toUpperCase())}/>
                <div className="confirm-hint">This confirms you want to archive these items now.</div>
              </div>
              <button className="btn btn-blue" onClick={handleExecute}
                disabled={executing||confirm!==CONFIRM_PHRASE}>
                {executing ? <><div className="spinner" style={{borderTopColor:'white'}}/>Archiving…</> : <><ArchiveBoxIcon/>Confirm Archive</>}
              </button>
            </>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="panel">
          <div className="success-box">
            <CheckCircleIcon style={{width:36,height:36,color:'#10c98e',margin:'0 auto 10px',display:'block'}}/>
            <div style={{fontSize:'.95rem',fontWeight:700,color:'#0f1123',marginBottom:4}}>Archive Complete</div>
            <div style={{fontSize:'.78rem',color:'#9094a8'}}>{result.message}</div>
            <div style={{display:'flex',gap:16,justifyContent:'center',marginTop:12}}>
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:'1.4rem',fontWeight:700,fontFamily:'JetBrains Mono',color:'#3b6ef8'}}>{result.results?.materials||0}</div>
                <div style={{fontSize:'.68rem',color:'#9094a8',fontWeight:600}}>Materials Archived</div>
              </div>
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:'1.4rem',fontWeight:700,fontFamily:'JetBrains Mono',color:'#3b6ef8'}}>{result.results?.quizzes||0}</div>
                <div style={{fontSize:'.68rem',color:'#9094a8',fontWeight:600}}>Quizzes Archived</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}